/**
 * Performance Marking Hooks
 * 
 * Lightweight hooks for marking performance milestones in components.
 * Zero overhead in production (marks are no-ops when not in DEV mode).
 */

import { useEffect, useRef, useCallback } from 'react';
import { 
  markColdStart, 
  startTabSwitchTracking, 
  markTabSwitch, 
  countRender,
  observeLCP,
  disconnectLCPObserver 
} from '@/lib/perfMetrics';

const IS_DEV = import.meta.env.DEV;

/**
 * Mark first shell render (after React hydrates/renders initial UI)
 * Use in App.tsx or root layout component
 */
export function useMarkShellRender() {
  const hasMarked = useRef(false);
  
  useEffect(() => {
    if (hasMarked.current) return;
    hasMarked.current = true;
    
    // Use double RAF to ensure we're past first paint
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        markColdStart('first_shell_render');
      });
    });
  }, []);
}

/**
 * Mark auth bootstrap complete
 * Use in AuthContext or BootstrapGate
 */
export function useMarkAuthReady(isReady: boolean) {
  const hasMarked = useRef(false);
  
  useEffect(() => {
    if (!isReady || hasMarked.current) return;
    hasMarked.current = true;
    
    markColdStart('auth_ready');
  }, [isReady]);
}

/**
 * Mark data fetch start/end for a component
 * Returns callbacks to mark fetch boundaries
 */
export function useDataFetchMarks() {
  const hasMarkedStart = useRef(false);
  const hasMarkedEnd = useRef(false);
  
  const markStart = useCallback(() => {
    if (hasMarkedStart.current) return;
    hasMarkedStart.current = true;
    markColdStart('data_fetch_start');
  }, []);
  
  const markEnd = useCallback(() => {
    if (hasMarkedEnd.current) return;
    hasMarkedEnd.current = true;
    markColdStart('data_fetch_end');
  }, []);
  
  return { markStart, markEnd };
}

/**
 * LCP Observer hook - attaches observer and marks LCP
 * Use in root component (App.tsx)
 */
export function useLCPObserver() {
  useEffect(() => {
    observeLCP();
    return () => disconnectLCPObserver();
  }, []);
}

/**
 * Track tab switch performance
 * Use in tab switching logic (DashboardTabContent)
 */
export function useTabSwitchPerf(activeTab: string, isCached: boolean = false) {
  const prevTab = useRef<string | null>(null);
  const hasMarkedPaint = useRef(false);
  
  useEffect(() => {
    // Skip initial render
    if (prevTab.current === null) {
      prevTab.current = activeTab;
      return;
    }
    
    // Only track actual tab changes
    if (prevTab.current !== activeTab) {
      startTabSwitchTracking(activeTab, isCached);
      hasMarkedPaint.current = false;
      prevTab.current = activeTab;
      
      // Mark first paint after tab content renders
      requestAnimationFrame(() => {
        markTabSwitch('chunk_ready');
        requestAnimationFrame(() => {
          if (!hasMarkedPaint.current) {
            markTabSwitch('first_paint');
            hasMarkedPaint.current = true;
          }
        });
      });
    }
  }, [activeTab, isCached]);
  
  // Mark data ready (call when tab data is loaded)
  const markDataReady = useCallback(() => {
    markTabSwitch('data_ready');
  }, []);
  
  return { markDataReady };
}

/**
 * Count component renders (DEV only)
 * Use in components you suspect are re-rendering too often
 */
export function useRenderCount(componentName: string) {
  if (IS_DEV) {
    countRender(componentName);
  }
}

/**
 * Mark first paint ref - attach to first visible element
 * Use for tab content to mark when content is visible
 */
export function useFirstPaintRef() {
  const hasMarked = useRef(false);
  
  const ref = useCallback((node: HTMLElement | null) => {
    if (!node || hasMarked.current) return;
    
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!hasMarked.current) {
          markTabSwitch('first_paint');
          hasMarked.current = true;
        }
      });
    });
  }, []);
  
  // Reset on unmount for next measurement
  useEffect(() => {
    return () => {
      hasMarked.current = false;
    };
  }, []);
  
  return ref;
}
